import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

/**
 * localStorage key holding the most recent session's timing record.
 *
 * Deliberately OUTSIDE the `mbrs-` / `__mbrs-` prefixes that
 * {@link clearCustomerDataCache} wipes, so the record SURVIVES logout and can
 * still be read afterwards (the whole point — "how long did the session that
 * just ended last?"). The record is diagnostic and non-sensitive: timestamps,
 * a `scope`, and an end `reason` — never identity, tokens, or PII — so leaving
 * it resident across users on a shared browser is harmless.
 */
export const SESSION_TIMING_STORAGE_KEY = 'yyc-session-tracking';

/** End reasons mirror {@link ForcedLogoutReason} plus the voluntary-logout case. */
export type SessionEndReason =
  | 'session-expired'
  | 'session-superseded'
  | 'logged-out'
  | 'scope-switched'
  | 'manual-logout';

/**
 * One session's timing record. Single record, overwritten on each login
 * ("last session only"). All time fields are stored twice — an ISO string for
 * human reading in DevTools and an epoch-ms number for duration math.
 */
export interface SessionTimingRecord {
  scope: 'customer' | 'dev';
  loginAt: string | null;
  loginAtMs: number | null;
  status: 'active' | 'ended';
  endedAt: string | null;
  endedAtMs: number | null;
  durationMs: number | null;
  durationHuman: string | null;
  endReason: SessionEndReason | null;
}

/**
 * TEMPORARY DIAGNOSTIC. Records when a session begins ({@link markLogin}) and
 * ends ({@link markEnd}), persists the result to localStorage, and prints a
 * one-line console summary on end — so we can see how long a session actually
 * lasts.
 *
 * Compatible with the "never persist auth state" rule in CLAUDE.md: this stores
 * only timestamps + scope + end reason, which cannot resurrect a dead session.
 *
 * Gated to non-production (matching {@link SessionEndDiagnosticsService}): both
 * persistence and the console line are silent when `environment.production`.
 *
 * Dependency-free (no `inject()`, no injected services) so it can be called from
 * {@link AuthService} with zero risk of a DI cycle and unit-tested without
 * TestBed. Every storage access is guarded — timing must NEVER break auth.
 */
@Injectable({ providedIn: 'root' })
export class SessionTimingService {
  /** Begin a session: write a fresh `active` record, overwriting any prior one. */
  markLogin(scope: 'customer' | 'dev'): void {
    if (!this.enabled()) {
      return;
    }
    const now = Date.now();
    this.write({
      scope,
      loginAt: new Date(now).toISOString(),
      loginAtMs: now,
      status: 'active',
      endedAt: null,
      endedAtMs: null,
      durationMs: null,
      durationHuman: null,
      endReason: null,
    });
  }

  /**
   * End a session: close the active record with its duration + reason and print
   * the summary. Idempotent — a record already `ended` is left untouched, so the
   * 401 burst that drives a forced logout cannot double-count. If no login was
   * observed (e.g. cookie-authenticated at boot), still records THAT a session
   * ended, with a null duration.
   */
  markEnd(reason: SessionEndReason): void {
    if (!this.enabled()) {
      return;
    }
    const existing = this.read();
    if (existing?.status === 'ended') {
      return; // already closed — don't overwrite the first end
    }

    const active = existing?.status === 'active' ? existing : null;
    const now = Date.now();
    const loginAtMs = active?.loginAtMs ?? null;
    const durationMs = loginAtMs != null ? now - loginAtMs : null;

    const record: SessionTimingRecord = {
      scope: active?.scope ?? 'customer',
      loginAt: active?.loginAt ?? null,
      loginAtMs,
      status: 'ended',
      endedAt: new Date(now).toISOString(),
      endedAtMs: now,
      durationMs,
      durationHuman: durationMs != null ? this.formatDuration(durationMs) : null,
      endReason: reason,
    };
    this.write(record);
    this.logSummary(record);
  }

  /** Read the current record, or null when absent / unreadable / corrupt. */
  read(): SessionTimingRecord | null {
    if (typeof window === 'undefined') {
      return null;
    }
    try {
      const raw = window.localStorage.getItem(SESSION_TIMING_STORAGE_KEY);
      return raw ? (JSON.parse(raw) as SessionTimingRecord) : null;
    } catch {
      return null;
    }
  }

  /** Enabled in non-production browser contexts only. */
  private enabled(): boolean {
    return !environment.production && typeof window !== 'undefined';
  }

  private write(record: SessionTimingRecord): void {
    try {
      window.localStorage.setItem(
        SESSION_TIMING_STORAGE_KEY,
        JSON.stringify(record)
      );
    } catch {
      // Private-mode browsers may refuse storage access; ignore. Timing must
      // never break the auth flow that called us.
    }
  }

  /** e.g. 2_779_000ms -> "46m19s", 3_723_000ms -> "1h02m03s", 8_000ms -> "8s". */
  private formatDuration(ms: number): string {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const pad = (part: number) => String(part).padStart(2, '0');
    if (hours > 0) {
      return `${hours}h${pad(minutes)}m${pad(seconds)}s`;
    }
    if (minutes > 0) {
      return `${minutes}m${pad(seconds)}s`;
    }
    return `${seconds}s`;
  }

  private logSummary(record: SessionTimingRecord): void {
    try {
      const login = record.loginAt ?? '(not observed)';
      const lasted = record.durationHuman ?? 'unknown';
      // eslint-disable-next-line no-console
      console.log(
        `[session-timing] login ${login} → ended ${record.endedAt}, ` +
          `lasted ${lasted}, reason: ${record.endReason}`
      );
    } catch {
      // Diagnostics must never break a teardown.
    }
  }
}
